<template>
  <!-- The :model="item" props are explicitly given to these inputs
  because this component is used in different ModelForms that edit
  different models. -->
  <div class="column q-gutter-sm">
    <div class="row q-col-gutter-sm items-center">
      <model-form-input
        :model="item"
        field="name"
        class="col-3"
        label="Name"
        :rules="[val => !!val || 'Field is required']"
        autofocus
      />
      <model-form-input
        :model="item"
        field="displayName"
        class="col-3"
        label="Display Name"
        :rules="[val => !!val || 'Field is required']"
      />
      <model-form-input
        :model="item"
        field="abbreviation"
        class="col-3"
        label="Abbreviation"
        :rules="[
          val => !!val || 'Field is required',
          val => val.length <= 4 || 'Please use maximum 4 characters'
        ]"
      />
      <model-form-checkbox
        :model="item"
        field="printingOptions.print_name"
        value-type="Boolean"
        class="col-3 q-pb-md"
        label="Print Name"
      >
        <q-tooltip>
          Prints the item name along with the item variation name on prep slips.
        </q-tooltip>
      </model-form-checkbox>
    </div>
    <div class="row q-col-gutter-sm q-mb-md">
      <div class="col-3">
        <image-uploader
          v-model="item.image"
          :image-url="item.imageUrl"
          :model="item"
          :disable="item.isTranslation"
        />
      </div>
      <div class="col-3">
        <color-picker
          color-name="custom"
          :color-hex="item.color"
          :disable="item.isTranslation"
          @update:model-value="value => item.color = value"
        />
      </div>
      <q-space />
      <div
        class="col-3"
        style="display: flex; flex-direction: column; justify-content: center; max-width: 200px; min-height: 190px;"
      >
        <register-button
          :button="button"
          :index="0"
          :edit-mode="false"
        />
      </div>
    </div>
    <div class="row q-col-gutter-sm">
      <model-form-input
        :model="item"
        field="description"
        class="col-12"
        type="textarea"
        label="Description"
      />
    </div>
    <div class="row q-col-gutter-sm items-center">
      <model-form-checkbox
        :model="item"
        field="availableOnline"
        class="col-3 q-pb-md"
        label="Available Online"
        :disable="item.hasCustomPrice"
      >
        <q-tooltip v-if="item.hasCustomPrice">
          Items with custom pricing cannot be sold online.
        </q-tooltip>
        <q-tooltip v-else>
          Show this item online.
        </q-tooltip>
      </model-form-checkbox>
      <model-form-checkbox
        v-if="item.category && item.category.hasAlcoholAcknowledgement"
        :model="item"
        field="ignoreAlcoholAcknowledgement"
        class="col-3 q-pb-md"
        label="Ignore Alcohol Acknowledgment"
      >
        <q-tooltip>
          Do not display alcohol purchase acknowledgment for this item.
        </q-tooltip>
      </model-form-checkbox>
      <model-form-checkbox
        :model="item"
        field="isFeature"
        class="col-3 q-pb-md"
        label="Feature"
        :disable="!item.availableOnline"
      >
        <q-tooltip>
          Highlight this item during checkout online.
        </q-tooltip>
      </model-form-checkbox>
      <div class="col-3" />
      <model-form-checkbox
        :model="item"
        field="hasCustomPrice"
        class="col-3 q-pb-md"
        label="Allow Custom Price"
      >
        <q-tooltip>
          Allow employees to enter a custom price when adding this item to an order,<br>overriding the default Item Variations and Modifiers prices below.
        </q-tooltip>
      </model-form-checkbox>
    </div>
    <div class="row q-col-gutter-sm items-center">
      <model-form-select
        :model="item"
        field="taxIds"
        class="col-6"
        label="Taxes"
        :options="taxes.data"
        option-label="name"
        option-value="id"
        map-options
        emit-value
        multiple
        use-chips
        clearable
      >
        <template #selected-item="scope">
          <q-chip
            v-if="scope.opt.name"
            :tabindex="scope.tabindex"
            :label="scope.opt.name"
            dense
            :disable="!item.overrideTaxes && item.itemClass && $_.includes(item.itemClass.taxIds, scope.opt.id)"
            removable
            @remove="scope.removeAtIndex(scope.index)"
          >
            <q-tooltip v-if="!item.overrideTaxes && item.itemClass && $_.includes(item.itemClass.taxIds, scope.opt.id)">
              Tax is inherited from Item Class. Override Taxes to remove.
            </q-tooltip>
          </q-chip>
        </template>
        <template #option="scope">
          <q-item
            v-bind="scope.itemProps"
            :disable="!item.overrideTaxes && item.itemClass && $_.includes(item.itemClass.taxIds, scope.opt.id)"
            v-on="scope.itemEvents"
          >
            <q-item-section>
              {{ scope.opt.name }}
            </q-item-section>
            <q-tooltip v-if="!item.overrideTaxes && item.itemClass && $_.includes(item.itemClass.taxIds, scope.opt.id)">
              Tax is inherited from Item Class. Override Taxes to remove.
            </q-tooltip>
          </q-item>
        </template>
      </model-form-select>
      <model-form-checkbox
        :model="item"
        field="overrideTaxes"
        class="col-3 q-pb-lg"
        label="Override Taxes"
      />
    </div>
    <div class="row q-col-gutter-sm items-center">
      <model-form-select
        :model="item"
        field="locationIds"
        class="col-6 q-pb-lg"
        :multiple="true"
        :options="$_.map(locations.data, location => { return { id: location.id, label: location.displayName } })"
        map-options
        emit-value
        option-value="id"
        stack-label
        label="Source Locations"
        use-chips
      />
      <model-form-checkbox
        :model="item"
        field="requiresPreparation"
        class="col-3 q-pb-lg"
        label="Requires Preparation"
      />
      <model-form-input
        :model="item"
        field="preparationTimeMinutes"
        class="col-3"
        label="Preparation Time (minutes)"
        type="number"
        step="1"
        min="0"
        max="120"
        :disable="!item.requiresPreparation"
        :rules="[val => val !== null || 'Field is required']"
      />
    </div>
    <div class="column q-col-gutter-sm items-center">
      <div class="col column">
        <div>
          <q-icon
            name="info_outline"
            size="md"
          />
          <span class="q-ml-sm">When an item is sold, the item will be sourced based on the following priority:</span>
        </div>
        <div class="q-mx-xl q-my-xs">
          <span>
            1. On-site sales for items with <i>*direct delivery types</i> will
            be sourced from the <i>selling location</i>, if included a source
            location above. Otherwise:<br>
            2. On-site sales for will be sourced from the first location
            (<u><b>with a printer</b></u>) listed above that is part of the
            selling location's <i>location group</i>. Otherwise:<br>
            3. On-site and online sales will be sourced from the first
            location listed above.<br><br>
            <span class="text-caption">* Direct delivery types are only
              available for items that are ready-to-serve or fast-prep, where the
              customer can be served without leaving the POS. Direct delivery
              types include "Direct to Customer" and "Register".</span>
          </span>
        </div>
      </div>
    </div>
    <div class="row q-col-gutter-sm items-center q-mb-lg">
      <model-form-toggle
        :model="item"
        field="overrideSkipDetailDialog"
        class="col-3"
        label="Override Dialog Control"
      >
        <q-tooltip>
          Dialog is automatically skipped if only one variation and no modifiers
        </q-tooltip>
      </model-form-toggle>
      <model-form-checkbox
        v-if="item.overrideSkipDetailDialog"
        :model="item"
        field="customSkipDetailDialog"
        class="col-3"
        label="Skip Detail Dialog"
      >
        <q-tooltip>
          Item will be directly added to the cart and not prompt for additional details.
        </q-tooltip>
      </model-form-checkbox>
      <template v-else>
        <q-checkbox
          :model-value="item.skipDetailDialog"
          class="col-3"
          label="Skip Detail Dialog"
          disable
          bottom-slots
        >
          <q-tooltip>
            Automatically skipped if only one variation and no modifiers, item will be directly added to the cart and not prompt for additional details.
          </q-tooltip>
        </q-checkbox>
      </template>
    </div>
    <div class="row q-col-gutter-md items-center">
      <model-form-select
        :model="item"
        field="itemClass"
        class="col-3"
        label="Class"
        :options="itemClasses.data"
        option-label="name"
        :display-value="item.itemClass && item.itemClass.name"
        :rules="[val => val !== null || itemClasses.data.length === 0 || 'Field is required']"
      />
      <model-form-select
        :model="item"
        field="category"
        class="col-3"
        label="Display Category"
        :options="categories.data"
        option-label="name"
        clearable
        emit-value
        :hint="categories && categories.data && categories.data.length > 0 ? undefined : 'No Categories configured'"
      />
      <model-form-select
        :model="item"
        field="preparationCategory"
        class="col-3"
        label="Preparation Category"
        :options="categories.data"
        option-label="name"
        clearable
        emit-value
        :hint="categories && categories.data && categories.data.length > 0 ? undefined : 'No Categories configured'"
      />
    </div>
  </div>
</template>

<script>
import RegisterButtonModel from 'src/models/register/RegisterButton'
import ColorPicker from 'src/modules/admin/components/ColorPicker'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import RegisterButton from 'src/modules/register/components/register-button/RegisterButton'

export default {
  name: 'ItemDetail',
  components: {
    ColorPicker,
    RegisterButton,
    ImageUploader
  },
  configuration: ['taxes', 'locations', 'itemClasses', 'categories'],
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      file: null
    }
  },
  computed: {
    button () {
      return new RegisterButtonModel({ type: 'item', item: this.item }, { noReference: true })
    }
  },
  watch: {
    file () {
      this.upload()
    },
    'item.hasCustomPrice' () {
      if (this.item.hasCustomPrice) {
        this.item.availableOnline = false
      }
    }
  }
}

</script>
